import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { Badge, Bleed, Box, ButtonLink, Card, Heading, Stack, Text } from '@eneco/sparky';
import type { BoxProps, TextProps } from '@eneco/sparky/types';

import type { HeroCardRebrandedContent } from './hero-card-rebranded-content.types';
import { HERO_CARD_SLOTS, resolveSlot } from './slots';

/**
 * `HeroCardRebranded` block for the CMS-agnostic lane, serving **both** providers. The rebranded
 * sibling of `HeroCard.tsx`: adds a badge, drops the ribbon (it has no swept `richText` family
 * array), and carries a second CTA.
 *
 * `buttonLink`/`secondaryButtonLink` carry their own display text on stored content, even though the
 * generated schema does not declare it (the dataset is schemaless). A link resolving to no text is
 * skipped rather than rendered unlabelled.
 *
 * Reuses `HERO_CARD_SLOTS` rather than declaring its own — the legacy component renders the same
 * `jss-hero-card-top`/`jss-hero-card-main` placeholders, so duplicating the list would only let the
 * two drift.
 */
const HeroCardRebranded: FC = () => {
  const { adapter } = useCms();
  const content = useContent<HeroCardRebrandedContent>();
  const rendering = useRendering();

  const footerBackgroundColor = useSetting('footerBackgroundColor') as BoxProps['backgroundColor'];
  const footerTextColor = useSetting('footerTextColor') as TextProps['color'];

  const richText = adapter.toRichText(content.content);
  const primary = linkWithUrl(content.buttonLink);
  const secondary = linkWithUrl(content.secondaryButtonLink);
  const topSlot = resolveSlot(rendering, HERO_CARD_SLOTS.top);
  const mainSlot = resolveSlot(rendering, HERO_CARD_SLOTS.main);

  return (
    <Card overflow="hidden">
      <Box padding={{ initial: '6', md: '10' }}>
        <Stack gap="6">
          {content.badgeText && (
            <Stack.Item>
              <Badge size="M">{content.badgeText}</Badge>
            </Stack.Item>
          )}

          {content.title && (
            <Heading size={{ initial: 'S', md: 'L' }} as="h1">
              {content.title}
            </Heading>
          )}

          {topSlot ? (
            <Box>
              <Placeholder name={topSlot} />
            </Box>
          ) : null}

          {richText && (
            <Bleed top="2">
              <Text size="BodyL">
                <RichText html={richText} />
              </Text>
            </Bleed>
          )}

          {mainSlot ? (
            <Box>
              <Placeholder name={mainSlot} />
            </Box>
          ) : null}

          {(primary?.text || secondary?.text) && (
            <Stack.Item grow={false}>
              <Stack direction={{ initial: 'column', md: 'row' }} gap="4">
                {primary?.text && (
                  <ButtonLink href={primary.href} target={primary.target}>
                    {primary.text}
                  </ButtonLink>
                )}
                {secondary?.text && (
                  <ButtonLink href={secondary.href} target={secondary.target} action="secondary">
                    {secondary.text}
                  </ButtonLink>
                )}
              </Stack>
            </Stack.Item>
          )}
        </Stack>
      </Box>
      {content.footerText && (
        <Box
          backgroundColor={footerBackgroundColor}
          borderBottomLeftRadius="m"
          borderBottomRightRadius="m"
          paddingX={{ initial: '6', md: '10' }}
          paddingY="3">
          <Stack alignX="center">
            <Text size="BodyS" display="inline" weight="bold" color={footerTextColor}>
              {content.footerText}
            </Text>
          </Stack>
        </Box>
      )}
    </Card>
  );
};

export default HeroCardRebranded;
